import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link2, AlertTriangle, CheckCircle, ChevronDown, ChevronUp, Unlink, RefreshCw, KeyRound, Copy, LogIn, Zap, Eye, EyeOff } from 'lucide-react';
import {
  useBrokerCurrent,
  useBrokerMappingAvailability,
  useBrokerProxyBinding,
  useMapBroker,
  useMySubscription,
  useSupportContact,
  useUnmapBroker,
  useUpdateBrokerCredentials,
} from '../../api/hooks';
import { SectionHeader, Spinner } from '../../components/ui';
import { BrokerSupportNotice } from '../../components/broker/BrokerSupportNotice';
import { BrokerCredentialFields } from '../../components/broker/BrokerCredentialFields';
import { useSessionStore } from '../../session/store';
import { fmtDateTime } from '../../lib/utils';
import { toast } from '../../store';
import { ROUTES } from '../../routes/constants';
import {
  BROKER_CONFIGS,
  BROKER_NAMES,
  isBrokerClientIdRequiredOnMap,
  shouldRenderBrokerClientId,
} from '../../config/brokerFields';
import { brokerApi } from '../../api/brokerClient';
import {
  computeBrokerAvailabilityMap,
  isBrokerSelectable,
  LOADING_MESSAGE as BROKER_AVAILABILITY_LOADING_MESSAGE,
  GENERIC_UNAVAILABLE_MESSAGE as BROKER_AVAILABILITY_ERROR_MESSAGE,
  sanitizeBrokerMapErrorMessage,
} from '../broker/brokerMappingAvailability';
import {
  INITIAL_BROKER_SELECTION,
  reserveSelectedBroker,
} from '../broker/brokerReservationSelection';

const PAID_PLAN_REQUIRED_MESSAGE = 'Broker mapping requires an active paid plan because static IP is required.';

function isPaidSubscription(subscription: any) {
  if (!subscription?.valid_till) return false;
  if (String(subscription.source || '').toUpperCase() === 'TRIAL') return false;
  return new Date(subscription.valid_till).getTime() >= Date.now();
}

// ─── Status badge ──────────────────────────────────────────────────────────────
function BrokerStatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    mapped:               'text-bull bg-bull/10 border-bull/30',
    unmapped:             'text-txt-muted bg-bg-surface border-bg-border',
    credentials_updated:  'text-amber bg-amber/10 border-amber/30',
  };
  return (
    <span className={`inline-flex px-2 py-0.5 rounded border text-2xs font-mono ${map[status] ?? 'text-txt-muted border-bg-border'}`}>
      {status}
    </span>
  );
}

// ─── Address family badge ──────────────────────────────────────────────────────
function AddressFamilyBadge({ family }: { family?: string | null }) {
  if (family !== 'IPV4' && family !== 'IPV6') return null;
  const label = family === 'IPV6' ? 'IPv6' : 'IPv4';
  return (
    <span className="inline-flex px-1.5 py-0.5 rounded border text-2xs font-mono text-info bg-info/10 border-info/30">
      {label}
    </span>
  );
}

export { BrokerCredentialFields };

// ─── Map broker form ───────────────────────────────────────────────────────────
function MapBrokerForm({ onSuccess }: { onSuccess: () => void }) {
  const mapBroker = useMapBroker();
  const {
    data: availabilityData,
    isLoading: availabilityLoading,
    isError: availabilityIsError,
    refetch: refetchAvailability,
  } = useBrokerMappingAvailability();
  // Self-service: no user_id, the backend resolves the caller's own Operator.
  const supportContact = useSupportContact();
  const availabilityMap = computeBrokerAvailabilityMap({
    brokerNames: BROKER_NAMES,
    availability: availabilityData,
    isLoading: availabilityLoading,
    isError: availabilityIsError,
  });
  const [brokerName, setBrokerName]     = useState(INITIAL_BROKER_SELECTION);
  const [brokerClientId, setBrokerClientId] = useState('');
  const [totpKey, setTotpKey]           = useState('');
  const [credentials, setCredentials]   = useState<Record<string, string>>({});
  const [error, setError]               = useState('');
  const [reservation, setReservation]   = useState<any>(null);
  const [reservationError, setReservationError] = useState<any>(null);
  const [reservationLoading, setReservationLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function reserve() {
      if (!brokerName) return;
      setReservationLoading(true);
      setReservationError(null);
      try {
        const data = await reserveSelectedBroker(brokerName, brokerApi.reserveProxyBinding);
        if (!cancelled) {
          setReservation(data);
          void refetchAvailability();
        }
      } catch (err: any) {
        if (!cancelled) {
          setReservation(null);
          setReservationError(err?.response?.data || { detail: err?.message || 'Failed to reserve static IP.' });
        }
      } finally {
        if (!cancelled) setReservationLoading(false);
      }
    }

    reserve();
    return () => { cancelled = true; };
  }, [brokerName, refetchAvailability]);

  const handleBrokerChange = (name: string) => {
    setBrokerName(name);
    setCredentials({}); // reset credential fields when broker changes
    setReservation(null);
    setReservationError(null);
  };

  const handleTopLevel = (field: 'broker_client_id' | 'totp_key', value: string) => {
    if (field === 'broker_client_id') setBrokerClientId(value);
    else setTotpKey(value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!brokerName) {
      setError('Select a broker.');
      return;
    }
    if (isBrokerClientIdRequiredOnMap(brokerName) && !brokerClientId.trim()) {
      setError(
        `${BROKER_CONFIGS[brokerName]?.accountIdentityLabel ?? 'Client ID'} is required for ${BROKER_CONFIGS[brokerName]?.label ?? brokerName}. Your broker login is verified against it before a token is saved.`,
      );
      return;
    }

    // Build payload — only include non-empty values
    const payload: Record<string, any> = { broker_name: brokerName.toUpperCase() };
    if (shouldRenderBrokerClientId(brokerName) && brokerClientId.trim()) {
      payload.broker_client_id = brokerClientId.trim();
    }
    if (totpKey.trim())        payload.totp_key         = totpKey.trim();

    // credentials object: only include non-empty entries
    const creds: Record<string, string> = {};
    for (const [k, v] of Object.entries(credentials)) {
      if (v.trim()) creds[k] = v.trim();
    }
    payload.credentials = creds;

    try {
      await mapBroker.mutateAsync(payload);
      toast.success('Broker mapped successfully');
      onSuccess();
    } catch (err: any) {
      setError(sanitizeBrokerMapErrorMessage(err));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 pt-3 border-t border-bg-border">
      {/* Broker select */}
      <div className="space-y-1">
        <label className="text-2xs font-mono text-txt-muted uppercase tracking-widest">Broker</label>
        <select
          value={brokerName}
          onChange={(e) => handleBrokerChange(e.target.value)}
          className="input-base"
          disabled={mapBroker.isPending || availabilityLoading || availabilityIsError}
        >
          <option value="">Select broker…</option>
          {BROKER_NAMES.map((b) => (
            <option key={b} value={b} disabled={!isBrokerSelectable(availabilityMap, b)}>
              {BROKER_CONFIGS[b]?.label ?? b}
            </option>
          ))}
        </select>
        {availabilityLoading && (
          <p className="text-2xs text-txt-muted">{BROKER_AVAILABILITY_LOADING_MESSAGE}</p>
        )}
        {!availabilityLoading && availabilityIsError && (
          <p className="text-2xs text-bear">{BROKER_AVAILABILITY_ERROR_MESSAGE}</p>
        )}
        <BrokerSupportNotice
          className="pt-1"
          supportEmail={supportContact.data?.support_email}
          supportPhone={supportContact.data?.support_phone}
          isLoading={supportContact.isLoading}
          isError={supportContact.isError}
        />
      </div>

      {brokerName && (
        <>
          <RedirectUrlRow brokerName={brokerName} />
          <BrokerCredentialFields
            brokerName={brokerName}
            brokerClientId={brokerClientId}
            totpKey={totpKey}
            credentials={credentials}
            onTopLevelChange={handleTopLevel}
            onCredentialChange={(k, v) => setCredentials((prev) => ({ ...prev, [k]: v }))}
            disabled={mapBroker.isPending}
            mode="map"
          />
        </>
      )}

      {reservationLoading && (
        <div className="text-xs rounded border border-sky-300 bg-sky-500/10 px-3 py-2 text-sky-200">
          Reserving a temporary static IP for {BROKER_CONFIGS[brokerName]?.label ?? brokerName}…
        </div>
      )}

      {!reservationLoading && reservation?.status === 'RESERVED' && (
        <div className="text-xs rounded border border-bull/30 bg-bull/10 px-3 py-2 text-bull">
          Temporary IP reserved: <span className="font-mono break-all">{reservation.public_ip}</span>
          {typeof reservation.remaining_seconds === 'number' && reservation.remaining_seconds > 0 && (
            <span className="text-txt-muted"> · expires in about {Math.max(1, Math.ceil(reservation.remaining_seconds / 60))} min</span>
          )}
          {(reservation.address_family === 'IPV4' || reservation.address_family === 'IPV6') && (
            <span className="text-txt-muted"> · {reservation.address_family === 'IPV6' ? 'IPv6' : 'IPv4'}</span>
          )}
        </div>
      )}

      {!reservationLoading && reservation?.status === 'BINDED' && (
        <div className="text-xs rounded border border-bull/30 bg-bull/10 px-3 py-2 text-bull">
          Active static IP: <span className="font-mono break-all">{reservation.public_ip}</span>
          {(reservation.address_family === 'IPV4' || reservation.address_family === 'IPV6') && (
            <span className="text-txt-muted"> · {reservation.address_family === 'IPV6' ? 'IPv6' : 'IPv4'}</span>
          )}
        </div>
      )}

      {!reservationLoading && reservation?.status === 'PENDING' && (
        <div className="text-xs rounded border border-amber/30 bg-amber/10 px-3 py-2 text-amber">
          Contact Support for the Static IP.
        </div>
      )}

      {reservationError?.error_code === 'RESERVATION_NOT_ALLOWED_FOR_TRIAL' && (
        <div className="text-xs rounded border border-amber/30 bg-amber/10 px-3 py-2 text-amber">
          Temporary IP reservation is available only for paid users.
        </div>
      )}

      {reservationError?.error_code === 'RESERVATION_LIMIT_REACHED' && (
        <div className="text-xs rounded border border-amber/30 bg-amber/10 px-3 py-2 text-amber">
          You already hold the maximum number of active temporary IP reservations.
        </div>
      )}

      {error && (
        <p className="text-xs text-bear bg-bear/10 border border-bear/30 rounded px-3 py-2">{error}</p>
      )}

      <button
        type="submit"
        disabled={mapBroker.isPending || !isBrokerSelectable(availabilityMap, brokerName)}
        className="btn-primary w-full"
      >
        {mapBroker.isPending ? 'Mapping...' : 'Map Broker'}
      </button>
    </form>
  );
}

// ─── Update credentials form ───────────────────────────────────────────────────
function UpdateCredentialsForm({ brokerName, currentClientId, onDone }: { brokerName: string; currentClientId?: string; onDone: () => void }) {
  const updateCreds = useUpdateBrokerCredentials();
  const [brokerClientId, setBrokerClientId] = useState(currentClientId ?? '');
  const [totpKey, setTotpKey]           = useState('');
  const [credentials, setCredentials]   = useState<Record<string, string>>({});
  const [error, setError]               = useState('');
  const [done, setDone]                 = useState(false);

  const handleTopLevel = (field: 'broker_client_id' | 'totp_key', value: string) => {
    if (field === 'broker_client_id') setBrokerClientId(value);
    else setTotpKey(value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Build credentials dict — only non-empty values (blank = keep existing)
    const creds: Record<string, string> = {};
    for (const [k, v] of Object.entries(credentials)) {
      if (v.trim()) creds[k] = v.trim();
    }
    const payload: Record<string, any> = {
      broker_name: brokerName.toUpperCase(),
      credentials: creds,
    };
    if (totpKey.trim()) payload.totp_key = totpKey.trim();
    if (shouldRenderBrokerClientId(brokerName) && brokerClientId.trim()) {
      payload.broker_client_id = brokerClientId.trim();
    }

    if (Object.keys(creds).length === 0 && !payload.totp_key && !payload.broker_client_id) {
      setError('Enter at least one value to update.');
      return;
    }

    try {
      await updateCreds.mutateAsync(payload);
      toast.success('Credentials updated');
      setDone(true);
      setTimeout(onDone, 1000);
    } catch (err: any) {
      setError(err?.message || 'Failed to update credentials.');
    }
  };

  if (done) return (
    <div className="flex items-center gap-2 text-bull text-xs py-2">
      <CheckCircle size={14} /> Credentials updated.
    </div>
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-3 pt-3 border-t border-bg-border">
      <BrokerCredentialFields
        brokerName={brokerName}
        brokerClientId={brokerClientId}
        totpKey={totpKey}
        credentials={credentials}
        onTopLevelChange={handleTopLevel}
        onCredentialChange={(k, v) => setCredentials((prev) => ({ ...prev, [k]: v }))}
        disabled={updateCreds.isPending}
        mode="update"
      />
      {error && <p className="text-xs text-bear bg-bear/10 border border-bear/30 rounded px-3 py-2">{error}</p>}
      <div className="flex gap-2 justify-end">
        <button type="button" onClick={onDone} className="btn-ghost">Cancel</button>
        <button type="submit" disabled={updateCreds.isPending} className="btn-primary">
          {updateCreds.isPending ? 'Updating...' : 'Update Credentials'}
        </button>
      </div>
    </form>
  );
}

// ─── Unmap confirm ─────────────────────────────────────────────────────────────
function UnmapConfirm({ onConfirm, onCancel, loading }: { onConfirm: () => void; onCancel: () => void; loading: boolean }) {
  return (
    <div className="p-4 space-y-3 border border-warn/30 bg-warn/5 rounded-lg">
      <div className="flex items-start gap-2 text-xs text-warn">
        <AlertTriangle size={14} className="mt-0.5 shrink-0" />
        <p>This will unmap your broker. Your identity lock will be released — email and phone will become editable again.</p>
      </div>
      <div className="flex gap-2 justify-end">
        <button onClick={onCancel} className="btn-ghost">Cancel</button>
        <button onClick={onConfirm} disabled={loading} className="text-xs px-3 py-1.5 rounded border border-bear/40 text-bear hover:bg-bear/10 transition-colors disabled:opacity-50">
          {loading ? 'Unmapping...' : 'Confirm Unmap'}
        </button>
      </div>
    </div>
  );
}

// ─── Redirect URL (OAuth brokers only) ────────────────────────────────────────
const OAUTH_BROKERS = new Set(['fyers', 'zerodha', 'angelone', 'nuvama', 'mastertrust', 'aliceblue']);

function RedirectUrlRow({ brokerName, className = '' }: { brokerName: string; className?: string }) {
  const [copied, setCopied] = useState(false);
  if (!brokerName) return null;
  const url = `${window.location.origin}/api/broker/oauth/callback/${brokerName}`;
  const helper = OAUTH_BROKERS.has(brokerName)
    ? "Register this in your broker's API app settings."
    : "Use this for broker app creation if redirect URL is mandatory. ALGOSYS uses credential/session flow for this broker.";

  function handleCopy() {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <div className={`rounded border border-amber/25 bg-amber/5 px-3 py-2 space-y-1 ${className}`}>
      <p className="text-2xs font-mono text-amber uppercase tracking-widest">Redirect URL</p>
      <p className="text-2xs text-txt-muted">{helper}</p>
      <div className="flex items-center gap-2 mt-1">
        <code className="flex-1 text-xs font-mono text-txt-primary break-all select-all">{url}</code>
        <button
          type="button"
          onClick={handleCopy}
          className="shrink-0 flex items-center gap-1 text-2xs px-2 py-1 rounded border border-bg-border text-txt-secondary hover:text-txt-primary hover:border-amber/40 transition-colors"
        >
          <Copy size={11} />
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>
    </div>
  );
}

// ─── Credential masked display ─────────────────────────────────────────────────
// Fixed 3-field layout: Client ID (plain), App ID (plain), Secret Key (hidden/toggleable).
function CredentialMaskedDisplay({ broker, proxyBinding, brokerProfile }: { broker: any; proxyBinding: any; brokerProfile: any }) {
  const [showSecret, setShowSecret]   = useState(false);
  const [copiedSecret, setCopiedSecret] = useState(false);
  const [copiedIP, setCopiedIP]       = useState(false);
  const creds = broker.credentials_masked ?? {};
  const brokerKey = (broker?.broker_name ?? '').toLowerCase();
  const brokerConfig = BROKER_CONFIGS[brokerKey];
  const fieldDefs = (brokerConfig?.fields ?? [])
    .filter((field) => field.key !== brokerConfig?.accountIdentityField);
  const accountIdentityLabel = brokerConfig?.accountIdentityLabel ?? 'User ID';
  const primaryField = fieldDefs[0];
  const secretField = fieldDefs.find((f) => f.type === 'password' && f.key !== primaryField?.key) ?? fieldDefs[1];
  const primaryLabel = primaryField?.label ?? 'Credential';
  const secretLabel = secretField?.label ?? 'Secret';
  const primaryValue = primaryField ? (creds[primaryField.key] ?? '—') : '—';
  const secretValue = secretField ? (creds[secretField.key] ?? '') : '';
  const brokerUserId = brokerProfile?.user_id || broker.broker_client_id || '—';

  function handleCopySecret() {
    navigator.clipboard.writeText(secretValue).then(() => {
      setCopiedSecret(true);
      setTimeout(() => setCopiedSecret(false), 2000);
    });
  }

  function handleCopyIP() {
    const ip = proxyBinding?.public_ip || '';
    navigator.clipboard.writeText(ip).then(() => {
      setCopiedIP(true);
      setTimeout(() => setCopiedIP(false), 2000);
    });
  }

  return (
    <div className="grid grid-cols-2 gap-3 text-xs">
      <div>
        <p className="text-txt-muted text-2xs font-mono uppercase tracking-widest">{accountIdentityLabel}</p>
        <p className="font-mono text-txt-secondary mt-0.5">{brokerUserId}</p>
      </div>
      <div>
        <p className="text-txt-muted text-2xs font-mono uppercase tracking-widest">{primaryLabel}</p>
        <p className="font-mono text-txt-secondary mt-0.5">{primaryValue}</p>
      </div>
      <div className="col-span-2">
        <p className="text-txt-muted text-2xs font-mono uppercase tracking-widest">{secretLabel}</p>
        <div className="flex items-center gap-2 mt-0.5">
          <p className="font-mono text-txt-secondary flex-1">
            {secretValue ? (showSecret ? secretValue : '••••••••••••') : '—'}
          </p>
          {secretValue && (
            <>
              <button
                type="button"
                onClick={() => setShowSecret((v) => !v)}
                className="text-txt-muted hover:text-txt-secondary transition-colors"
                aria-label={showSecret ? 'Hide secret key' : 'Show secret key'}
              >
                {showSecret ? <EyeOff size={13} /> : <Eye size={13} />}
              </button>
              {showSecret && (
                <button
                  type="button"
                  onClick={handleCopySecret}
                  className="shrink-0 flex items-center gap-1 text-2xs px-2 py-1 rounded border border-bg-border text-txt-secondary hover:text-txt-primary hover:border-amber/40 transition-colors"
                >
                  <Copy size={11} />
                  {copiedSecret ? 'Copied!' : 'Copy'}
                </button>
              )}
            </>
          )}
        </div>
      </div>
      <div>
        <p className="text-txt-muted text-2xs font-mono uppercase tracking-widest">Mapped At</p>
        <p className="font-mono text-txt-secondary mt-0.5">{fmtDateTime(broker.mapped_at)}</p>
      </div>
      <div>
        <p className="text-txt-muted text-2xs font-mono uppercase tracking-widest">Last Updated</p>
        <p className="font-mono text-txt-secondary mt-0.5">{fmtDateTime(broker.updated_at)}</p>
      </div>
      {/* Static IP — show above redirect URL when broker is mapped */}
      <div className="col-span-2 rounded border border-bg-border px-3 py-2 space-y-1">
        <p className="text-2xs font-mono uppercase tracking-widest text-txt-muted flex items-center gap-1.5">
          Assigned Static IP
          <AddressFamilyBadge family={proxyBinding?.address_family} />
        </p>
        {proxyBinding?.status === 'BINDED' && proxyBinding?.public_ip ? (
          <div className="flex items-center gap-2 mt-0.5 min-w-0">
            <code className="text-xs font-mono text-txt-primary break-all">{proxyBinding.public_ip}</code>
            <button
              type="button"
              onClick={handleCopyIP}
              className="shrink-0 flex items-center gap-1 text-2xs px-2 py-1 rounded border border-bg-border text-txt-secondary hover:text-txt-primary hover:border-amber/40 transition-colors"
            >
              <Copy size={11} />
              {copiedIP ? 'Copied!' : 'Copy'}
            </button>
          </div>
        ) : proxyBinding?.status === 'PENDING' ? (
          <p className="text-2xs text-txt-muted italic mt-0.5">Pending allocation</p>
        ) : (
          <p className="text-2xs text-txt-muted italic mt-0.5">—</p>
        )}
      </div>
      <RedirectUrlRow brokerName={(broker.broker_name ?? '').toLowerCase()} className="col-span-2" />
    </div>
  );
}

// ─── Generate token section ────────────────────────────────────────────────────
const OAUTH_BROKER_NAMES        = new Set(['fyers', 'zerodha', 'angelone', 'nuvama', 'mastertrust', 'aliceblue']);
const AUTO_REFRESH_BROKER_NAMES = new Set<string>();
const MANUAL_TOTP_BROKER_NAMES  = new Set(['kotakneo']);

function GenerateTokenSection({ broker, onRefresh }: { broker: any; onRefresh?: () => void }) {
  const queryClient = useQueryClient();
  const [loading, setLoading]             = useState(false);
  const [done, setDone]                   = useState(false);
  const [err, setErr]                     = useState('');
  const [showTotpInput, setShowTotpInput] = useState(false);
  const [totp, setTotp]                   = useState('');
  const bn: string = (broker.broker_name ?? '').toLowerCase();

  async function handleOAuth() {
    setErr(''); setLoading(true);
    try {
      const data = await brokerApi.getOAuthUrl(bn.toUpperCase());
      if (data?.oauth_url) window.location.href = data.oauth_url;
    } catch (e: any) {
      setErr(e?.response?.data?.detail || 'Could not generate login URL.');
    } finally {
      setLoading(false);
    }
  }

  async function handleAutoRefresh() {
    setErr(''); setLoading(true);
    try {
      const data = await brokerApi.testConnection({ broker_name: bn.toUpperCase() });
      if (data?.success) {
        setDone(true);
        toast.success('Session token generated');
      } else {
        setErr(data?.message || 'Session generation failed.');
      }
    } catch (e: any) {
      setErr(e?.response?.data?.detail || 'Request failed.');
    } finally {
      setLoading(false);
    }
  }

  async function handleKotakRefresh() {
    setErr('');
    setLoading(true);
    try {
      const cleanTotp = totp.trim();
      await brokerApi.refreshKotakSession(cleanTotp);
      setDone(true);
      setShowTotpInput(false);
      setTotp('');
      toast.success('Kotak session token generated');
      onRefresh?.();
      queryClient.invalidateQueries({ queryKey: ['brokerStatus'] });
      queryClient.invalidateQueries({ queryKey: ['brokerFunds'] });
      queryClient.invalidateQueries({ queryKey: ['brokerFundsHealth'] });
    } catch (e: any) {
      setErr(
        e?.response?.data?.detail ||
        e?.response?.data?.message ||
        'Request failed.'
      );
    } finally {
      setLoading(false);
    }
  }

  const label = BROKER_CONFIGS[bn]?.label ?? bn;

  return (
    <div className="pt-2 border-t border-bg-border space-y-2">
      <p className="text-2xs font-mono text-txt-muted uppercase tracking-widest">
        {bn === 'aliceblue' ? 'Session' : 'Daily Token'}
      </p>

      {done ? (
        <div className="flex items-center gap-2 text-bull text-xs">
          <CheckCircle size={13} /> {bn === 'aliceblue' ? 'Session stored. Verify read-only Profile/Funds before use.' : 'Session active for today.'}
        </div>
      ) : OAUTH_BROKER_NAMES.has(bn) ? (
        <button
          onClick={handleOAuth}
          disabled={loading}
          className="flex items-center gap-2 text-xs px-3 py-1.5 rounded border border-amber/40 text-amber hover:bg-amber/10 transition-colors disabled:opacity-50"
        >
          <LogIn size={13} />
          {loading ? 'Redirecting…' : bn === 'aliceblue' ? `Log in to ${label} (generate session)` : `Log in to ${label} (generate today's token)`}
        </button>
      ) : AUTO_REFRESH_BROKER_NAMES.has(bn) ? (
        <button
          onClick={handleAutoRefresh}
          disabled={loading}
          className="flex items-center gap-2 text-xs px-3 py-1.5 rounded border border-amber/40 text-amber hover:bg-amber/10 transition-colors disabled:opacity-50"
        >
          <Zap size={13} />
          {loading ? 'Generating…' : `Auto-generate ${label} session`}
        </button>
      ) : MANUAL_TOTP_BROKER_NAMES.has(bn) ? (
        <div className="space-y-2">
          {!showTotpInput ? (
            <button
              onClick={() => setShowTotpInput(true)}
              className="flex items-center gap-2 text-xs px-3 py-1.5 rounded border border-amber/40 text-amber hover:bg-amber/10 transition-colors"
            >
              <KeyRound size={13} />
              Generate Session
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <input
                type="password"
                value={totp}
                onChange={(e) => setTotp(e.target.value)}
                placeholder="6-digit TOTP"
                maxLength={6}
                className="input-base w-32 text-xs"
              />
              <button
                onClick={handleKotakRefresh}
                disabled={loading || totp.trim().length !== 6}
                className="text-xs px-3 py-1.5 rounded border border-bull/40 text-bull hover:bg-bull/10 transition-colors disabled:opacity-50"
              >
                {loading ? 'Submitting…' : 'Submit'}
              </button>
              <button
                onClick={() => { setShowTotpInput(false); setTotp(''); setErr(''); }}
                className="text-xs px-3 py-1.5 rounded border border-bg-border text-txt-muted hover:bg-bg-surface transition-colors"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      ) : null}

      {err && <p className="text-xs text-bear">{err}</p>}
    </div>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────────
export default function BrokerPage() {
  const role = useSessionStore((s) => s.role);
  const isAdmin = role === 'admin';
  const isEndUser = role === 'end_user';
  const { data: broker, isLoading, error, refetch } = useBrokerCurrent();
  const { data: proxyBinding } = useBrokerProxyBinding();
  const { data: subscription } = useMySubscription();
  const unmapBroker = useUnmapBroker();
  const hasActiveBroker = Boolean(broker?.has_active_broker && broker?.active_broker);
  const canMapBroker = !isEndUser || isPaidSubscription(subscription);
  const { data: brokerProfile } = useQuery({
    queryKey: ['brokerProfile'],
    queryFn: brokerApi.getProfile,
    enabled: hasActiveBroker,
    staleTime: 30_000,
    retry: 1,
  });

  const [showUpdateForm, setShowUpdateForm] = useState(false);
  const [showUnmapConfirm, setShowUnmapConfirm] = useState(false);
  const cardSubtitle = brokerProfile?.user_id || broker?.broker_client_id || 'Credentials configured';

  const handleUnmap = async () => {
    try {
      await unmapBroker.mutateAsync(undefined);
      toast.success('Broker unmapped');
      setShowUnmapConfirm(false);
    } catch (err: any) {
      toast.error(err?.message || 'Failed to unmap broker.');
    }
  };

  return (
    <div className="max-w-xl space-y-5">
      <SectionHeader
        title="Broker"
        subtitle="Manage your broker mapping and API credentials"
      />

      {/* ── Loading ── */}
      {isLoading && (
        <div className="flex justify-center py-12"><Spinner /></div>
      )}

      {/* ── Error ── */}
      {!isLoading && error && (
        <div className="panel p-5 space-y-3">
          <div className="flex items-center gap-2 text-bear text-sm">
            <AlertTriangle size={16} />
            <span>Failed to load broker status.</span>
          </div>
          <p className="text-xs text-txt-muted">{(error as any)?.message}</p>
          <button onClick={() => refetch()} className="btn-ghost flex items-center gap-1.5">
            <RefreshCw size={13} /> Retry
          </button>
        </div>
      )}

      {/* ── Empty state: no broker ── */}
      {!isLoading && !error && !hasActiveBroker && (
        <div className="panel p-5 space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-bg-surface flex items-center justify-center">
              <Link2 size={18} className="text-txt-muted" />
            </div>
            <div>
              <p className="font-semibold text-txt-primary text-sm">No broker mapped</p>
              <p className="text-xs text-txt-muted">Map a broker to enable live trading.</p>
            </div>
          </div>
          {canMapBroker ? (
            <MapBrokerForm onSuccess={() => refetch()} />
          ) : (
            <div className="rounded border border-amber/30 bg-amber/10 px-3 py-2 text-xs text-amber space-y-2">
              <p>{PAID_PLAN_REQUIRED_MESSAGE}</p>
              <a href={ROUTES.SUBSCRIPTION} className="inline-flex text-warn hover:text-warn/80">
                View subscription
              </a>
            </div>
          )}
        </div>
      )}

      {/* ── Mapped state ── */}
      {!isLoading && !error && hasActiveBroker && broker && (
        <>
          {/* Broker card */}
          <div className="panel p-5 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-bull/10 flex items-center justify-center">
                  <Link2 size={18} className="text-bull" />
                </div>
                <div>
                  <p className="font-semibold text-txt-primary text-sm capitalize">
                    {BROKER_CONFIGS[(broker.broker_name ?? '').toLowerCase()]?.label ?? broker.broker_name}
                  </p>
                  <p className="text-xs font-mono text-txt-muted">{cardSubtitle}</p>
                </div>
              </div>
              <BrokerStatusBadge status={broker.status} />
            </div>

            {/* Credential summary */}
            <div className="pt-2 border-t border-bg-border">
              <CredentialMaskedDisplay broker={broker} proxyBinding={proxyBinding} brokerProfile={brokerProfile} />
            </div>

            {/* Generate daily token */}
            <GenerateTokenSection broker={broker} onRefresh={refetch} />

            {/* Update credentials */}
            <div className="pt-2 border-t border-bg-border">
              <button
                onClick={() => { setShowUpdateForm((v) => !v); setShowUnmapConfirm(false); }}
                className="flex items-center gap-2 text-xs text-amber hover:underline transition-colors"
              >
                {showUpdateForm ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                Update Credentials
              </button>
              {showUpdateForm && (
                <UpdateCredentialsForm
                  brokerName={(broker.broker_name ?? '').toLowerCase()}
                  currentClientId={broker.broker_client_id ?? ''}
                  onDone={() => setShowUpdateForm(false)}
                />
              )}
            </div>

            {/* Unmap — admin only */}
            {isAdmin && (
            <div className="pt-2 border-t border-bg-border">
              {!showUnmapConfirm ? (
                <button
                  onClick={() => { setShowUnmapConfirm(true); setShowUpdateForm(false); }}
                  className="flex items-center gap-2 text-xs text-bear hover:underline transition-colors"
                >
                  <Unlink size={13} /> Unmap Broker
                </button>
              ) : (
                <UnmapConfirm
                  onConfirm={handleUnmap}
                  onCancel={() => setShowUnmapConfirm(false)}
                  loading={unmapBroker.isPending}
                />
              )}
            </div>
            )}
          </div>

        </>
      )}
    </div>
  );
}
